import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiBarChartBoxLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const PrometheusGrafanaInfoCard = () => {
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations || {};

  const grafanaUrl = annotations['monitoring.infrastructure/grafana-url'];
  const prometheusUrl = annotations['monitoring.infrastructure/prometheus-url'];
  const grafanaCredentials = annotations['monitoring.infrastructure/grafana-credentials'];
  const redisMetricsEndpoint = annotations['monitoring.infrastructure/redis-metrics-endpoint'];
  const sshPrivateKeyEncoded = annotations['monitoring.infrastructure/ssh-private-key'];
  const sshCommand = annotations['monitoring.infrastructure/ssh-command'];

  const awsInstanceId = annotations['aws.infrastructure/instance-id'];
  const awsPublicIp = annotations['aws.infrastructure/public-ip'];
  const awsPrivateIp = annotations['aws.infrastructure/private-ip'];
  const awsInstanceType = annotations['aws.infrastructure/instance-type'];
  const awsRegion = annotations['aws.infrastructure/region'];
  const awsSsmCommand = annotations['aws.infrastructure/ssm-command'];

  const gcpInstanceId = annotations['gcp.infrastructure/instance-id'];
  const gcpPublicIp = annotations['gcp.infrastructure/public-ip'];
  const gcpPrivateIp = annotations['gcp.infrastructure/private-ip'];
  const gcpMachineType = annotations['gcp.infrastructure/machine-type'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const gcpConsoleUrl = annotations['gcp.infrastructure/console-url'];
  const gcpGcloudSshCommand = annotations['gcp.infrastructure/gcloud-ssh-command'];

  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];

  if (!templateType?.includes('prom-grafana')) {
    return null;
  }

  const isAWS = templateType?.startsWith('aws-');

  const instanceId = awsInstanceId || gcpInstanceId;
  const publicIp = awsPublicIp || gcpPublicIp;
  const instanceType = awsInstanceType || gcpMachineType;
  const region = awsRegion || gcpZone;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'monitoring_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <InfoCard
      title="📊 Prometheus & Grafana Monitoring Stack"
      subheader={`Environment: ${environmentName || 'N/A'}`}
      icon={<RiBarChartBoxLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="🌐 Access URLs">
          {grafanaUrl && (
            <InfraField
              label="📈 Grafana Dashboard:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={grafanaUrl}>{grafanaUrl}</ExternalLink>
                  <CopyButton text={grafanaUrl} />
                </span>
              }
              mono={false}
            />
          )}
          {grafanaCredentials && (
            <CopyableValue label="🔑 Grafana Credentials:" value={grafanaCredentials} />
          )}
          {prometheusUrl && (
            <InfraField
              label="📊 Prometheus UI:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={prometheusUrl}>{prometheusUrl}</ExternalLink>
                  <CopyButton text={prometheusUrl} />
                </span>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title={`🖥️ ${isAWS ? 'EC2' : 'Compute'} Instance`}>
          {instanceId && (
            <InfraField
              label="Instance ID:"
              value={
                <span className={styles.copyRow}>
                  {isAWS ? (
                    <ExternalLink
                      href={`https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion}#InstanceDetails:instanceId=${instanceId}`}
                    >
                      {instanceId}
                    </ExternalLink>
                  ) : (
                    <ExternalLink href={gcpConsoleUrl}>{instanceId}</ExternalLink>
                  )}
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label={isAWS ? 'Instance Type:' : 'Machine Type:'}
              value={instanceType}
            />
          )}
          {region && (
            <InfraField label={isAWS ? 'Region:' : 'Zone:'} value={region} />
          )}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
          {(awsPrivateIp || gcpPrivateIp) && (
            <CopyableValue label="Private IP:" value={awsPrivateIp || gcpPrivateIp || ''} />
          )}
        </InfraSection>

        {(sshPrivateKey || sshCommand || awsSsmCommand || gcpGcloudSshCommand) && (
          <InfraSection title="🔧 SSH Access">
            {sshPrivateKey && (
              <>
                <InfraField label="SSH Private Key:" value="" mono={false} />
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
                <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                  💡 After downloading, run <code>chmod 400 monitoring_key.pem</code>
                </Text>
              </>
            )}
            {sshCommand && <CopyableValue label="SSH Command:" value={sshCommand} />}
            {awsSsmCommand && <CopyableValue label="SSM Command:" value={awsSsmCommand} />}
            {gcpGcloudSshCommand && (
              <CopyableValue label="gcloud SSH Command:" value={gcpGcloudSshCommand} />
            )}
          </InfraSection>
        )}

        {redisMetricsEndpoint && (
          <InfraSection title="📡 Monitoring Configuration">
            <CopyableValue label="Redis Metrics Endpoint:" value={redisMetricsEndpoint} />
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
